import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { faBell } from '@fortawesome/free-solid-svg-icons';

import { AlertEditorComponent } from '../../components/alert-editor/alert-editor.component';

import {
  SuiDialogConfig,
  DialogCloseReason,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';

import { Alert } from 'src/app/core/models/alert.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData {
  alert: Alert;
}
interface DialogOutput {}

@Component({
  selector: 'app-alert-editor-dialog',
  templateUrl: './alert-editor-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    forwardRef(() => AlertEditorComponent),
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class AlertEditorDialogComponent extends SimpleDialogBase<
  DialogData,
  DialogOutput
> {
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '30%',
    minWidth: 600,
    maxWidth: 800,
  };
  @ViewChild('alertEditor') alertEditor: AlertEditorComponent;

  protected destroyRef = inject(DestroyRef);

  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  private readonly formValueChanges = signal<any>(null);
  private readonly formStatusChanges = signal<any>(null);

  public isDirty = computed(() => {
    this.formValueChanges();
    return this.alertEditor.formGroup.dirty;
  });

  public isValid = computed(() => {
    return this.formStatusChanges() === 'VALID';
  });

  get alert(): Alert {
    return this.dialogData.alert;
  }

  objectTitle = this.translateService.instant('popup.action-title.edit-alert');
  objectIcon = faBell;

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.alertEditor.dialogInlineError = this.showApiError;
    const form = this.alertEditor.formGroup;

    form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.formValueChanges.set(v));
    form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => this.formStatusChanges.set(s));

    this.formValueChanges.set(form.value);
    this.formStatusChanges.set(form.status);

    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  /** Waits for the save (WAP-1976): closes on success, keeps the form with the refusal in the band. */
  protected override performSave(): Promise<void> {
    const formData = this.alertEditor.formGroup.value;
    this.clearApiError();
    this.saving.set(true);
    return Promise.resolve(this.alertEditor.submit())
      .then(() =>
        this.dirtyHandler.closeImmediately({
          reason: DialogCloseReason.Save,
          data: formData,
        }),
      )
      .catch(() => undefined)
      .finally(() => this.saving.set(false));
  }

  save(): void {
    if (!this.isValid()) {
      this.alertEditor.formGroup.markAllAsTouched();
      return;
    }

    void this.performSave();
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
